import { useContext } from "react";
import { AuthContext, type AuthContextValue } from "@/providers/auth-provider";

const ROLE_HIERARCHY: Record<string, number> = {
  viewer: 0,
  employee: 1,
  manager: 2,
  owner: 3,
};

const PERMISSION_MAP: Record<string, string> = {
  "view:dashboard": "viewer",
  "view:orders": "viewer",
  "view:products": "viewer",
  "manage:orders": "employee",
  "manage:products": "employee",
  "manage:coupons": "employee",
  "manage:reviews": "employee",
  "manage:settings": "manager",
  "manage:team": "manager",
  "manage:legal": "manager",
  "manage:billing": "owner",
  "manage:danger": "owner",
};

export interface UseAuthReturn extends AuthContextValue {
  readonly hasPermission: (permission: string) => boolean;
  readonly hasRole: (minRole: string) => boolean;
}

export function useAuth(): UseAuthReturn {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  const hasRole = (minRole: string): boolean => {
    if (context.user === null) return false;
    const userLevel = ROLE_HIERARCHY[context.user.role] ?? 0;
    const requiredLevel = ROLE_HIERARCHY[minRole] ?? 0;
    return userLevel >= requiredLevel;
  };

  const hasPermission = (permission: string): boolean => {
    if (context.user === null) return false;
    const requiredRole = PERMISSION_MAP[permission];
    if (requiredRole === undefined) return false;
    return hasRole(requiredRole);
  };

  return {
    ...context,
    hasPermission,
    hasRole,
  };
}
